/**
 * Turning an error code into a sentence a person can act on.
 *
 * A quota denial and a suppression block are normal, expected outcomes of a
 * system that deliberately refuses to send. They get calm, explanatory copy that
 * names the limit and says when it clears. Only genuine faults read as faults.
 *
 * Copy rules: no em dashes, no semicolon-joined clauses, no internal identifiers.
 */
import { ApiError, SESSION_ENDED_MESSAGE, isApiError } from './api'
import { formatDateTime, formatLocalDateTime, relativeTimeFrom } from './format'
import { QUOTA_REASON, SUPPRESSION_REASON } from './labels'
import type { QuotaDeniedDetails, SuppressedDetails, ValidationIssue } from './types'

export type ErrorTone = 'blocked' | 'invalid' | 'fault' | 'offline'

export interface ErrorDescription {
  /** Short heading, sentence case. */
  title: string
  /** One or two sentences saying what happened and what to do. */
  detail: string
  tone: ErrorTone
  /** True when trying again later is genuinely likely to work. */
  retryable: boolean
  /** When the block lifts, if the server told us. */
  clearsAt?: string
  /** Field-level problems, for forms. */
  issues?: ValidationIssue[]
  /** The raw code, for the small print. */
  code: string
}

// ---------------------------------------------------------------------------
// Quota denials and suppression blocks
// ---------------------------------------------------------------------------

/**
 * The words come from `labels.ts`, which is also what the badge on the record
 * reads, so an operator is never told two different stories about the same
 * refusal. Only the numbers that vary per denial are added here.
 */
function quotaCopy(details: QuotaDeniedDetails): { title: string; detail: string } {
  const copy = QUOTA_REASON[details.reason]
  const used = typeof details.used === 'number' ? details.used : undefined
  const cap = typeof details.cap === 'number' ? details.cap : undefined
  const counted = used !== undefined && cap !== undefined ? ` You are at ${used} of ${cap}.` : ''
  const when = quotaWhen(details)

  if (copy === undefined) {
    return {
      title: 'Blocked by a safety limit',
      detail: `The platform refused this action to keep the account safe.${counted}${when}`,
    }
  }
  return { title: copy.label, detail: `${copy.detail}${counted}${when}` }
}

/**
 * When a refusal lifts, on the reader's own clock, in the sentence itself.
 *
 * In the sentence because a toast shows only the sentence, so a time kept in
 * `clearsAt` alone never reached the person the toast was for. On their clock
 * with the zone named, because the server sends UTC and the phones keep their
 * own, and a bare time could be read on any of the three. The quiet days are
 * named as themselves, since "can go again" undersells a wait of days.
 */
function quotaWhen(details: QuotaDeniedDetails): string {
  const quiet = localMoment(details.quietUntil)
  if (quiet !== null) return ` Its quiet days end at ${quiet}, on your clock.`
  const retry = localMoment(details.retryAt)
  return retry === null ? '' : ` It can go again at ${retry}, on your clock.`
}

function localMoment(value: unknown): string | null {
  const iso = timestampFrom(value)
  if (iso === undefined) return null
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? null : formatLocalDateTime(at)
}

function suppressionCopy(details: SuppressedDetails): { title: string; detail: string } {
  const copy = SUPPRESSION_REASON[details.reason]
  const brand = typeof details.blockingBrandName === 'string' ? details.blockingBrandName : undefined
  if (copy === undefined) {
    return {
      title: 'This person cannot be contacted',
      detail: 'The platform is holding them back, and the reason is recorded on their record.',
    }
  }
  const who = brand === undefined ? '' : ` The brand holding the contact is ${brand}.`
  return { title: copy.label, detail: `${copy.detail}${who}` }
}

// ---------------------------------------------------------------------------
// The main entry point
// ---------------------------------------------------------------------------

function issuesFrom(details: Record<string, unknown> | undefined): ValidationIssue[] | undefined {
  const raw = details?.issues ?? details?.errors
  if (!Array.isArray(raw)) return undefined
  const issues = raw.filter(
    (i): i is ValidationIssue => typeof i === 'object' && i !== null && 'message' in i,
  )
  return issues.length > 0 ? issues : undefined
}

function timestampFrom(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** Describe any thrown value in language a person can act on. */
/**
 * An error from a service behind this app that says itself whether asking
 * again can help: the prospects pages' `ProspectingError` (unreachable, or a
 * 429) carries `isTransient`. Read by shape, so this file does not import a
 * page's client.
 */
function isTransientError(error: unknown): boolean {
  return error instanceof Error && (error as { isTransient?: unknown }).isTransient === true
}

export function describeError(error: unknown): ErrorDescription {
  if (!isApiError(error)) {
    const message = error instanceof Error ? error.message : 'Something went wrong.'
    return {
      title: 'Something went wrong',
      detail: message,
      tone: 'fault',
      retryable: isTransientError(error),
      code: 'UNKNOWN',
    }
  }

  const details = error.details ?? {}

  if (error.code === 'QUOTA_DENIED') {
    const quota = details as unknown as QuotaDeniedDetails
    const copy = quotaCopy(quota)
    return {
      ...copy,
      tone: 'blocked',
      retryable: quota.reason !== 'ACCOUNT_HEALTH' && quota.reason !== 'SESSION_LIMIT',
      clearsAt: timestampFrom(quota.retryAt) ?? timestampFrom(quota.quietUntil),
      code: error.code,
    }
  }

  if (error.code === 'SUPPRESSED') {
    const suppressed = details as unknown as SuppressedDetails
    const copy = suppressionCopy(suppressed)
    return {
      ...copy,
      tone: 'blocked',
      retryable: suppressed.reason === 'CROSS_BRAND_ACTIVE',
      clearsAt: timestampFrom(suppressed.clearsAt),
      code: error.code,
    }
  }

  switch (error.code) {
    case 'VALIDATION_FAILED':
      return {
        title: 'That did not pass validation',
        detail: error.message || 'Some of what you sent is not usable yet. The problems are listed below.',
        tone: 'invalid',
        retryable: false,
        issues: issuesFrom(details),
        code: error.code,
      }
    case 'UNAUTHORIZED': {
      // A 401 is two different events wearing one status. An expired cookie
      // arrives with no body, and the client fills in the session-ended
      // sentence. A refused sign-in arrives with a sentence the server wrote on
      // purpose: the password was wrong, the code was already used, the
      // authenticator has to be set up first. That sentence is the only thing
      // that tells somebody what to do differently, so it is never replaced.
      const said = error.message.trim()
      const sessionEnded = said === '' || said === SESSION_ENDED_MESSAGE
      return {
        title: sessionEnded ? 'Your session has ended' : 'That was refused',
        detail: sessionEnded ? 'Sign in again to carry on. Nothing you had open is lost.' : said,
        tone: 'fault',
        retryable: false,
        code: error.code,
      }
    }
    case 'FORBIDDEN':
      return {
        title: 'You do not have access to this',
        detail:
          'Your account is not allowed to see or change this. An admin can grant it if you need it.',
        tone: 'fault',
        retryable: false,
        code: error.code,
      }
    case 'NOT_FOUND': {
      // The brand hint is right for the records a brand owns, which is nearly
      // everything. It is wrong and confusing for the handful of things that
      // belong to the build rather than to a brand, so a route that knows
      // better says so in `hint` and that wins.
      const hint = typeof details.hint === 'string' && details.hint !== '' ? details.hint : null
      return {
        title: 'Not found',
        detail:
          hint ??
          'This either never existed or belongs to a different brand. Check the brand selected at the top of the page.',
        tone: 'fault',
        retryable: false,
        code: error.code,
      }
    }
    case 'CONFLICT':
      return {
        title: 'That clashes with the current state',
        detail:
          error.message ||
          'Something changed since this page loaded. Reload it and look at where things stand before trying again.',
        tone: 'invalid',
        retryable: false,
        code: error.code,
      }
    case 'PAYLOAD_TOO_LARGE':
      return {
        title: 'That file is too big',
        detail: 'Split it into smaller files and upload them one after another.',
        tone: 'invalid',
        retryable: false,
        code: error.code,
      }
    case 'RATE_LIMITED':
      return {
        title: 'Too many requests for now',
        detail: 'Give it a moment and try again.',
        tone: 'blocked',
        retryable: true,
        clearsAt: timestampFrom(details.retryAt),
        code: error.code,
      }
    case 'UPSTREAM_FAILED': {
      // A route that knows what went wrong now says so, in a sentence it wrote on
      // purpose: the model declined, or answered with something unusable. Before
      // 2026-09-14 every one of those arrived here as "Internal server error" and
      // was replaced with the sentence below, which told somebody facing a
      // refusal that trying again in a minute was safe. The fallback is kept for
      // the failures that still carry no sentence of their own.
      const said = error.message.trim()
      const explained = said !== '' && said !== 'Internal server error'
      const reason = typeof details.reason === 'string' ? details.reason : null
      return {
        title:
          reason === 'refused'
            ? 'The model declined'
            : reason === 'unusable'
              ? 'Nothing usable came back'
              : 'A service we depend on did not answer',
        detail: explained
          ? said
          : 'This is outside the platform. Nothing was half-done, so trying again in a minute is safe.',
        tone: 'fault',
        // Asking the same thing again gets the same refusal.
        retryable: reason !== 'refused',
        code: error.code,
      }
    }
    case 'LLM_ALLOWANCE_SPENT': {
      // Its own case because it clears in hours, not seconds. The one fact that
      // tells somebody what to do is when to come back, so it is in the sentence
      // itself and not only in `clearsAt`, which a toast never shows. A return
      // time is only promised when the service named one. Otherwise the sentence
      // says when a request will ask again, because nothing asks on its own.
      //
      // `clearsAt` is the named time and nothing else. A panel prints it as
      // "Clears at", and a probe time printed there is wrong every time: a press
      // after it asks, is refused again, and moves the probe further out.
      const known = details.resetKnown === true
      const resumeAt = known ? timestampFrom(details.resumeAt) : undefined
      // A server from before `nextTryAt` sent the probe time in `resumeAt`.
      const nextTryAt = timestampFrom(details.nextTryAt) ?? (known ? undefined : timestampFrom(details.resumeAt))
      const back = describeClearsAt(resumeAt)
      const next = describeClearsAt(nextTryAt)
      const spent = 'The model subscription this platform writes with has used its allowance'
      const nothing = 'Nothing was written and nothing was saved.'
      return {
        title: 'The writing allowance is used up',
        detail:
          back !== null
            ? `${spent}. ${nothing} It comes back at ${back}.`
            : next !== null
              ? `${spent} and has not said when it comes back. ${nothing} A request after ${next} asks it again.`
              : `${spent}. ${nothing} Try again later.`,
        tone: 'blocked',
        retryable: true,
        ...(resumeAt === undefined ? {} : { clearsAt: resumeAt }),
        code: error.code,
      }
    }
    case 'NETWORK_UNREACHABLE':
      return {
        title: 'Cannot reach the server',
        detail: 'Check your connection. The page will work again as soon as the server answers.',
        tone: 'offline',
        retryable: true,
        code: error.code,
      }
    case 'RESPONSE_UNREADABLE':
      return {
        title: 'The server sent something unexpected',
        detail: 'This is a bug worth reporting. Reloading usually gets you moving again.',
        tone: 'fault',
        retryable: true,
        code: error.code,
      }
    case 'UNAVAILABLE':
      return {
        title: 'The service is briefly unavailable',
        detail: 'It is probably restarting. Try again shortly.',
        tone: 'offline',
        retryable: true,
        code: error.code,
      }
    default:
      return {
        title: 'Something went wrong',
        detail: error.message || 'The server rejected the request and did not say why.',
        tone: error.isTransient ? 'offline' : 'fault',
        retryable: error.isTransient,
        code: error.code,
      }
  }
}

/** One-line version, handy for toasts. */
export function errorMessage(error: unknown): string {
  const described = describeError(error)
  return described.detail ? `${described.title}. ${described.detail}` : described.title
}

/**
 * The heading a failed request is shown under.
 *
 * A page passes its own heading when it knows better than the code what was
 * being attempted, and for almost every code it does. A spent writing allowance
 * is the exception. On 2026-09-13 the Describe it panel showed "Nothing came
 * back" over a sentence saying the subscription was used up, which reads as a
 * fault in the model and sends somebody back to the button. What stops the
 * request there is the allowance, whatever the page was doing, so that heading
 * wins.
 */
const HEADING_OUTRANKS_PAGE: ReadonlySet<string> = new Set(['LLM_ALLOWANCE_SPENT'])

export function errorHeading<T>(described: Pick<ErrorDescription, 'code' | 'title'>, pageTitle: T | null | undefined): T | string {
  if (pageTitle === null || pageTitle === undefined || HEADING_OUTRANKS_PAGE.has(described.code)) return described.title
  return pageTitle
}

/** "clears at 18:40, about 2 hours from now" for the calm blocked-state panels. */
export function describeClearsAt(iso: string | undefined, now: Date = new Date()): string | null {
  if (!iso) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  return `${formatDateTime(at)}, ${relativeTimeFrom(at, now)}`
}

/** Narrowing helper for pages that want to branch on a specific quota reason. */
export function quotaReasonOf(error: unknown): QuotaDeniedDetails['reason'] | undefined {
  if (!isApiError(error) || error.code !== 'QUOTA_DENIED') return undefined
  return (error.details as unknown as QuotaDeniedDetails | undefined)?.reason
}

/** Narrowing helper for pages that want to branch on a specific suppression reason. */
export function suppressionReasonOf(error: unknown): SuppressedDetails['reason'] | undefined {
  if (!isApiError(error) || error.code !== 'SUPPRESSED') return undefined
  return (error.details as unknown as SuppressedDetails | undefined)?.reason
}

export { ApiError, isApiError }
